



































































import { AdvancedSelect, ShortTypeBadge, SvgImage } from '@/app/components';
import { useAxios, useQueryParams } from '@/app/composable';
import { useRouter } from '@/app/composable/router';
import { S } from '@/app/utilities';
import { computed, defineComponent, PropType, ref, watchEffect } from '@vue/composition-api';
import { AxiosResponse } from 'axios';
import { isNil } from 'ramda';
import { useAssetColumnMetrics } from '../../composable/asset-column-metrics';
import { Asset } from '../../types';
import ColumnMetricsCard from './ColumnMetricsCard.vue';
import ColumnMetricsTabMenu from './ColumnMetricsTabMenu.vue';

import { MetricsAPI } from '../../api';
import {
    CategoricalColumnMetrics,
    DatetimeColumnMetrics,
    MetricField,
    NumericalColumnMetrics,
} from './../../types/data-profiling.type';

export default defineComponent({
    name: 'ColumnMetrics',
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        columns: {
            type: Array as PropType<{ name: string; type?: string }[]>,
            default: () => [],
        },
    },
    components: {
        AdvancedSelect,
        ShortTypeBadge,
        SvgImage,
        ColumnMetricsCard,
        ColumnMetricsTabMenu,
    },
    setup(props: any, { emit, root }: { emit: any; root: any }) {
        const MAX_COLUMNS_ALLOWED: number = 5;

        const router = useRouter();
        const { get, set } = useQueryParams(root, router, 'assets:view');
        const errorResponse = ref<{ message?: string; status: number } | undefined>();

        const { exec, loading } = useAxios(true);

        const {
            numericalMetrics,
            datetimeMetrics,
            categoricalMetrics,
            getMetricDisplayValue,
            getValueDescription,
            getValueNotPrecise,
        } = useAssetColumnMetrics();

        const selectedColumns = ref<string[]>([]);
        const metricsList = ref<any>([]);

        // Columns for select dropdown
        const columnOptions = computed(() =>
            props.columns.map((col: { name: string; type?: string }) => ({
                key: col.name,
                label: col.name,
                type: col.type ?? '',
            })),
        );

        const columnType = (columnName: string) => {
            const col = props.columns.find((c: { name: string; type?: string }) => c.name === columnName);
            return col ? col.type ?? '' : '';
        };

        const tabsAvailable = computed(() =>
            selectedColumns.value.map((col: string) => {
                return {
                    key: col,
                    name: col,
                    type: columnType(col),
                };
            }),
        );

        const activeColumn = ref<string | undefined>(undefined);
        const setActiveTabAfterLoad = ref<boolean>(false);

        const removeColumn = (key: string) => {
            const index = selectedColumns.value.indexOf(key);
            if (index < 0) return;
            setActiveTabAfterLoad.value = true;
            selectedColumns.value.splice(index, 1);
            if (selectedColumns.value.length === 0) {
                updateActiveColumn();
                updateRouter();
            } else {
                if (activeColumn.value === key) updateActiveColumn(selectedColumns.value[0]);
                updateRouter(selectedColumns.value.join(','));
            }
        };

        const columnSelected = (updatedList: any, itemClicked: any) => {
            setActiveTabAfterLoad.value = true;
            if (itemClicked) {
                if (selectedColumns.value.length === 1) updateActiveColumn(selectedColumns.value[0]);

                updateRouter(selectedColumns.value.join(','));
            } else {
                if (selectedColumns.value.length === 0) {
                    updateRouter();
                    updateActiveColumn();
                } else {
                    if (activeColumn.value && !selectedColumns.value.includes(activeColumn.value)) {
                        updateActiveColumn(selectedColumns.value[0]);
                    }
                    updateRouter(selectedColumns.value.join(','));
                }
            }
        };

        const updateActiveColumn = (newColumn?: string) => {
            activeColumn.value = newColumn;
            if (newColumn) fetchColumnMetrics();
        };

        const updateRouter = (newValue?: string) => {
            set('columns-metrics', newValue, undefined);
        };

        const fetchColumnMetrics = () => {
            errorResponse.value = undefined;
            if (isNil(props.asset.id) || !activeColumn.value) return;

            exec(MetricsAPI.getAssetColumnMetrics(+props.asset.id, activeColumn.value))
                .then(
                    (
                        res:
                            | AxiosResponse<NumericalColumnMetrics | DatetimeColumnMetrics | CategoricalColumnMetrics>
                            | undefined,
                    ) => {
                        if (!res) return;
                        let metrics: MetricField[] = numericalMetrics;
                        if (['date', 'datetime', 'time'].includes(res.data.type)) {
                            metrics = datetimeMetrics;
                        } else if (['string', 'boolean', 'unknown'].includes(res.data.type)) {
                            metrics = categoricalMetrics;
                        }
                        metricsList.value = metrics.map((metric) => ({
                            ...metric,
                            value: getMetricDisplayValue(res.data, metric.key),
                            valueDescription: getValueDescription(res.data, metric.key),
                            valueNotPrecise: getValueNotPrecise(res.data, metric.key),
                        }));
                        emit('new-exploratory-metrics', false);
                    },
                )
                .catch((e) => {
                    errorResponse.value = {
                        message: e?.response?.statusText,
                        status: e.response.status,
                    };
                });
        };

        // Set default selected columns from query params and set activeColumn
        const selectColumnsFromUrl = () => {
            if (!props.columns || props.columns.length === 0 || selectedColumns.value.length > 0) return;

            // Get columns from url
            const columnsParam = get('columns-metrics');
            if (!isNil(columnsParam)) {
                columnsParam.split(',').forEach((col: string) => {
                    col = col.trim();
                    if (selectedColumns.value.length < 5 && props.columns.find((c: { name: string }) => c.name === col))
                        selectedColumns.value.push(col);
                });
                if (!activeColumn.value && selectedColumns.value.length) updateActiveColumn(selectedColumns.value[0]);
            } else {
                if (
                    !activeColumn.value &&
                    !selectedColumns.value.length &&
                    columnOptions.value.length &&
                    !setActiveTabAfterLoad.value
                ) {
                    updateActiveColumn(columnOptions.value[0].label);
                    updateRouter(columnOptions.value[0].label);
                    selectedColumns.value.push(columnOptions.value[0].label);
                    setActiveTabAfterLoad.value = true;
                }
            }
        };

        watchEffect(() => {
            selectColumnsFromUrl();
        });

        return {
            S,
            selectedColumns,
            columnOptions,
            activeColumn,
            removeColumn,
            updateRouter,
            columnType,
            columnSelected,
            updateActiveColumn,
            MAX_COLUMNS_ALLOWED,
            metricsList,
            loading,
            errorResponse,
            tabsAvailable,
        };
    },
});
